import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { MatTooltip, type TooltipPosition } from '@angular/material/tooltip';

export type BzTooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

/**
 * Logical sides for left and right, as the CSS-only version had
 * (`inset-inline-start`), so a tooltip still mirrors in Hebrew on its own.
 */
const POSITIONS: Record<BzTooltipPlacement, TooltipPosition> = {
  top: 'above',
  bottom: 'below',
  left: 'before',
  right: 'after',
};

/** Styled in styles/material.css: the bubble renders in the overlay, outside this component's stylesheet. */
const BUBBLE_CLASS = 'bz-tooltip-bubble';

/**
 * Wraps its content and shows `content` in a bubble on hover or focus.
 *
 * Built on `matTooltip`, applied to the host as a host directive, so the
 * bubble renders in the CDK overlay rather than inside the page. The first
 * version was CSS-only — a bubble positioned off `:host` — and any ancestor
 * with `overflow` other than `visible` cut it off: a `bz-expansion-panel`
 * (Material clips the panel to animate its height) and the scrolling body of
 * a `bz-dialog-layout`, which is exactly where info icons tend to sit.
 * Working around that by un-clipping the ancestor broke the panel's own
 * animation instead. The overlay also brings viewport-edge flipping and
 * `aria-describedby` on the host, both of which the CSS version lacked.
 *
 * Still wraps content in an element, same as the legacy `bringoz-tooltip`
 * did, so the call sites did not change. `matTooltip` only reacts to focus on
 * the element it sits on, while the focusable thing is usually the wrapped
 * content — a button, a link — so focus anywhere inside the host opens the
 * bubble too, as `:focus-within` did before.
 */
@Component({
  selector: 'bz-tooltip',
  templateUrl: './bz-tooltip.html',
  styleUrl: './bz-tooltip.scss',
  hostDirectives: [MatTooltip],
  host: {
    '[class]': 'hostClass()',
    '(focusin)': 'tooltip.show()',
    '(focusout)': 'tooltip.hide()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzTooltip {
  readonly content = input.required<string>();
  readonly placement = input<BzTooltipPlacement>('top');

  protected readonly tooltip = inject(MatTooltip);

  protected readonly hostClass = computed(() => `bz-tooltip bz-tooltip--${this.placement()}`);

  constructor() {
    this.tooltip.tooltipClass = BUBBLE_CLASS;
    effect(() => {
      this.tooltip.message = this.content();
      this.tooltip.position = POSITIONS[this.placement()];
    });
  }
}
